import React, { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

interface InHouseDialogProps {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  children: React.ReactNode;
  actions?: React.ReactNode;
  closeLabel?: string;
  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  closeDisabled?: boolean;
  contentVariant?: 'default' | 'message';
}

const InHouseDialog: React.FC<InHouseDialogProps> = ({
  open,
  onClose,
  title,
  children,
  actions,
  closeLabel = 'Close',
  maxWidth = 'sm',
  fullWidth = true,
  closeDisabled = false,
  contentVariant = 'default',
}) => {
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('sm'));
  const titleHorizontalInset = isCompact ? '10px' : '16px';
  const lifecyclePrefix = 'lifecycle-debug';

  useEffect(() => {
    console.log(`[${lifecyclePrefix}] InHouseDialog mount`, { open, title: String(title) });

    return () => {
      console.log(`[${lifecyclePrefix}] InHouseDialog unmount`, { title: String(title) });
    };
  }, []);

  useEffect(() => {
    console.log(`[${lifecyclePrefix}] InHouseDialog open change`, {
      open,
      title: String(title),
    });
  }, [open, title]);

  const handleDialogClose: DialogProps['onClose'] = (_event, reason) => {
    if (closeDisabled && (reason === 'backdropClick' || reason === 'escapeKeyDown')) {
      return;
    }

    onClose();
  };

  const contentPanel = (
    <Box
      data-testid="inhouse-dialog-content-panel"
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: isCompact ? 1.25 : 1.75,
        py: isCompact ? 1.25 : 1.75,
        backgroundColor: '#111',
        borderRadius: 1,
      }}
    >
      {contentVariant === 'message' ? (
        <Typography
          variant="h5"
          sx={{
            fontSize: isCompact ? '1.15rem' : '1.35rem',
            fontWeight: 500,
            lineHeight: 1.5,
            textAlign: 'center',
            display: 'block',
            pt: '1.5em',
            pb: '1.5em',
          }}
        >
          {children}
        </Typography>
      ) : (
        children
      )}

      {actions && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1,
            width: '100%',
            mt: contentVariant === 'message' ? 1.75 : 2,
          }}
        >
          {actions}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={closeDisabled}
      PaperProps={{
        sx: {
          m: isCompact ? 1 : 3,
          maxHeight: isCompact ? 'calc(100% - 16px)' : 'calc(100% - 48px)',
          width: isCompact ? 'calc(100% - 16px)' : undefined,
        },
      }}
    >
      <DialogTitle
        sx={{
          px: titleHorizontalInset,
          py: isCompact ? 1 : 1.5,
          pr: isCompact ? 6 : 7,
          position: 'relative',
        }}
      >
        <Typography
          component="span"
          variant="h5"
          sx={{
            display: 'block',
            fontSize: isCompact ? '1.15rem' : '1.35rem',
            fontWeight: 500,
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          disabled={closeDisabled}
          sx={{
            position: 'absolute',
            right: titleHorizontalInset,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'grey.500',
            p: 0,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: isCompact ? 1.25 : 2,
          py: isCompact ? 0.5 : 1,
          overflowY: 'auto',
          '&:first-of-type': {
            pt: isCompact ? 0.5 : 1,
          },
        }}
      >
        {contentPanel}
      </DialogContent>

      <DialogActions
        sx={{
          px: isCompact ? 1.25 : 2,
          py: isCompact ? 1 : 1.5,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            width: '100%',
          }}
        >
          <Button onClick={onClose} disabled={closeDisabled}>
            {closeLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default InHouseDialog;
